import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { ApolloProvider } from "@apollo/client/react";
import { LocalizationProvider } from "@fluent/react";
import { getThemeCSS, lightTheme } from "@atlas-int/tokens";
import { apolloClient } from "../src/graphql/apolloClient";
import l10n from "../src/contextProviders/getFluentLocalizationContext";
import { TutorialProvider } from "../src/components/tutorial/TutorialContext";
import ErrorPage from "../src/pages/ErrorPage";
import { buildMeta } from "./routeMeta";

import "../src/index.css";
import "../src/fonts.css";

// Critical font weights — fetched in parallel with HTML parsing so they're
// ready when CSS resolves, instead of waiting for the stylesheet to be
// parsed before the @font-face URLs are discovered. Without preload, the
// browser shows the fallback font for ~200ms and then swaps to Source Sans
// Pro, which reads as a flash. We preload the two weights the hero uses
// on every page (regular body + semibold heading); other weights swap in
// later, below the fold, where the change is less noticeable.
import sourceSansProRegular from "../src/fonts/source-sans-pro-v13-latin-regular.woff2?url";
import sourceSansPro600 from "../src/fonts/source-sans-pro-v13-latin-600.woff2?url";

export function links() {
  return [
    {
      rel: "preload",
      href: sourceSansProRegular,
      as: "font",
      type: "font/woff2",
      crossOrigin: "anonymous",
    },
    {
      rel: "preload",
      href: sourceSansPro600,
      as: "font",
      type: "font/woff2",
      crossOrigin: "anonymous",
    },
    { rel: "icon", href: "/Harvard-shield-favicon.png" },
    { rel: "apple-touch-icon", href: "/Harvard-shield-favicon.png" },
    { rel: "manifest", href: "/manifest.json" },
  ];
}

export function meta(args) {
  // Defaults pulled from openGraphData.js so non-prerendered routes
  // (and crawlers that hit the SPA fallback) get the same baseline tags
  // the OG injector uses.
  return buildMeta(
    {
      description:
        "Visualize global trade data and economic growth opportunities for every country.",
    },
    args,
  );
}

// Theme CSS as a string emitted in <head>. Previously injected via emotion
// `<Global>` from <ThemeVars>, which renders `<style>` tags inline in <body>
// during prerender — that gave a flash of unstyled content before the
// browser parsed past those tags. Inlining in <head> avoids the flash.
const THEME_CSS = getThemeCSS(import.meta.env.PROD ? lightTheme : undefined);

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <style dangerouslySetInnerHTML={{ __html: THEME_CSS }} />
        {/* Runtime config. Written by Ansible at deploy time
            (roles/atlas-front-end/tasks/inject-config.yml); served by a
            Vite middleware in dev (see vite.config.ts). Synchronous so
            window.__ATLAS_CONFIG__ is set before any module imports
            getRuntimeConfig(). */}
        <script src="/config.js" />
      </head>
      <body>
        <noscript>You need to enable JavaScript to run this app.</noscript>
        {children}
        <div
          id="offscreen-rendering"
          style={{ position: "absolute", top: 0, left: -9999 }}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <TutorialProvider>
      <ApolloProvider client={apolloClient}>
        <LocalizationProvider l10n={l10n}>
          <Outlet />
        </LocalizationProvider>
      </ApolloProvider>
    </TutorialProvider>
  );
}

export function ErrorBoundary() {
  return <ErrorPage />;
}

// Neutral fallback used to prerender __spa-fallback.html (the shell served
// for any non-prerendered route, e.g. /countries) and as the default during
// the initial hydration window before route discovery completes. Section-
// specific shells (e.g. the /explore skeleton) live on their own routes so
// they don't get embedded into the SPA fallback HTML and cause hydration
// mismatches on other sections.
export function HydrateFallback() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontFamily: "Source Sans Pro, sans-serif",
      }}
    >
      Loading...
    </div>
  );
}
